import React, { useState } from "react";
import styled, { keyframes } from "styled-components";
import SEO from "../components/SEO";
import BackgroundDesign from "../components/BackgroundDesign";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFilter,
  faMapMarkerAlt,
  faCalendarAlt,
} from "@fortawesome/free-solid-svg-icons";
import { Link } from "react-router-dom";

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const PageWrapper = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  position: relative;
`;

const PageHero = styled.section`
  position: relative;
  height: 100vh;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.1)),
      url(/images/photo_5802988364859820042_y.jpg) center/cover no-repeat;

    filter: brightness(0.3);
    z-index: 1;
  }

  &::after {
    content: "";
    position: absolute;
    bottom: -1px;
    left: 0;
    right: 0;
    height: 150px;
    background: linear-gradient(
      to top,
      ${(props) => props.theme.colors.background},
      transparent
    );
    z-index: 2;
  }
`;

const PageHeroContent = styled.div`
  position: relative;
  z-index: 3;
  text-align: center;
  color: white;
  max-width: 800px;
  padding: 0 2rem;
  animation: ${fadeUp} 1s ease-out;
`;

const PageTitle = styled.h1`
  font-size: 4rem;
  font-weight: 800;
  margin-bottom: 1.5rem;
  text-transform: uppercase;
  letter-spacing: 2px;

  span {
    color: ${(props) => props.theme.colors.primary};
  }
`;

const PageSubtitle = styled.p`
  font-size: 1.2rem;
  line-height: 1.8;
  opacity: 0.9;
  margin-bottom: 2rem;
`;

const MainContent = styled.main`
  flex: 1;
  width: 100%;
  padding: 10rem 0;
  position: relative;
  z-index: 1;
`;

const FiltersSection = styled.section`
  max-width: 1200px;
  margin: -150px auto 4rem;
  padding: 2rem;
  background: white;
  border-radius: 20px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  position: relative;
  z-index: 3;
  animation: ${fadeUp} 0.8s ease-out;
`;

const SearchBar = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 2rem;

  input {
    flex: 1;
    padding: 1rem 1.5rem;
    border: 2px solid #eee;
    border-radius: 10px;
    font-size: 1rem;
    transition: all 0.3s ease;

    &:focus {
      outline: none;
      border-color: ${(props) => props.theme.colors.primary};
      box-shadow: 0 0 0 3px ${(props) => props.theme.colors.primary}22;
    }
  }
`;

const FilterButton = styled.button`
  background: ${(props) =>
    props.$active ? props.theme.colors.primary : "white"};
  color: ${(props) => (props.$active ? "white" : props.theme.colors.text)};
  border: 2px solid
    ${(props) => (props.$active ? props.theme.colors.primary : "#eee")};
  padding: 0.75rem 1.5rem;
  border-radius: 10px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &:hover {
    border-color: ${(props) => props.theme.colors.primary};
    color: ${(props) => (props.$active ? "white" : props.theme.colors.primary)};
  }
`;

const FilterTags = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
`;

const CampaignsGrid = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 2rem;
`;

const CampaignCard = styled.div.attrs(props => ({
  style: {
    animationDelay: `${props.index * 0.1}s`,
  }
}))`
  background: white;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  animation: ${fadeUp} 0.8s ease-out;
  opacity: 0;
  animation-fill-mode: forwards;
  display: flex;
  flex-direction: column;
  height: 100%;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 40px rgba(0, 0, 0, 0.15);
  }
`;

// Add a spacer div to push the button to the bottom
const Spacer = styled.div`
  flex: 1;
`;

const CampaignImage = styled.div.attrs(props => ({
  style: {
    backgroundImage: `url(${props.image})`,
  }
}))`
  height: 200px;
  background-size: cover;
  background-position: center;
  position: relative;

  &::after {
    content: "";
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 50%;
    background: linear-gradient(to top, rgba(0, 0, 0, 0.7), transparent);
  }
`;

const CampaignContent = styled.div`
  padding: 2rem;
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const CampaignTitle = styled.h3`
  font-size: 1.5rem;
  color: ${(props) => props.theme.colors.text};
  margin-bottom: 1rem;
  font-weight: 700;
`;

const CampaignMeta = styled.div`
  display: flex;
  gap: 1.5rem;
  margin-bottom: 1.5rem;
  color: #666;
  font-size: 0.9rem;

  div {
    display: flex;
    align-items: center;
    gap: 0.5rem;

    svg {
      color: ${(props) => props.theme.colors.primary};
    }
  }
`;

const ProgressBar = styled.div`
  height: 8px;
  background: #eee;
  border-radius: 4px;
  margin-bottom: 1rem;
  overflow: hidden;
`;

const ProgressFill = styled.div`
  background: #b5873a;
  height: 100%;
  width: ${(props) => props.$progress || 0}%;
  border-radius: 4px;
  transition: width 1s ease;
`;

const CampaignStats = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 1.5rem;

  div {
    text-align: center;

    h4 {
      font-size: 1.2rem;
      color: ${(props) => props.theme.colors.text};
      margin-bottom: 0.25rem;
    }

    p {
      color: #666;
      font-size: 0.9rem;
    }
  }
`;

const DonateButton = styled(Link)`
  display: block;
  width: 100%;
  background: ${(props) => props.theme.colors.primary};
  color: white;
  border: none;
  padding: 1rem;
  border-radius: 10px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  text-align: center;
  text-decoration: none;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 20px ${(props) => props.theme.colors.primary}33;
  }
`;

const actions = [
  {
    id: 1,
    title: "We Cleaned. We Cared. We Protected",
    location: "Ain Sebaa Beach, Casablanca",
    date: "June 22, 2025",
    image: `${process.env.PUBLIC_URL}/images/OurActions/beach-cleanup.webp`,
    raised: 0,
    goal: 0,
    donors: 0,
    daysLeft: 0,
    progress: 0,
    showStats: false,
    category: "Environment",
  }
];

const categories = ["All", "Education", "Healthcare", "Food", "Shelter", "Environment"];

const CampaignsPage = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [activeCategory, setActiveCategory] = useState("All");

  const filteredActions = actions
    .filter(
      (action) =>
        action.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        action.location.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .filter(
      (action) =>
        activeCategory === "All" || action.category === activeCategory
    );

  return (
    <PageWrapper>
      <BackgroundDesign />
      <SEO
        title="Our Actions | The Big Heart Association | Humanitarian Campaigns Morocco"
        description="Explore The Big Heart Association's humanitarian actions across Morocco — beach cleanups, educational initiatives, medical caravans, and community support programs."
        url="/actions"
        breadcrumbs={[{ name: "Home", url: "/" }, { name: "Our Actions", url: "/actions" }]}
        noOrganizationSchema
      />
      <PageHero>
        <PageHeroContent>
          <PageTitle>
            Our <span>Actions</span>
          </PageTitle>
          <PageSubtitle>
            Join us in making a difference. Support our actions and help us
            create positive change in communities.
          </PageSubtitle>
        </PageHeroContent>
      </PageHero>
      <MainContent>
        <FiltersSection>
          <SearchBar>
            <input
              type="text"
              placeholder="Search actions..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <FilterButton>
              <FontAwesomeIcon icon={faFilter} />
              Filters
            </FilterButton>
          </SearchBar>
          <FilterTags>
            {categories.map((category) => (
              <FilterButton
                key={category}
                $active={activeCategory === category}
                onClick={() => setActiveCategory(category)}
              >
                {category}
              </FilterButton>
            ))}
          </FilterTags>
        </FiltersSection>
        <CampaignsGrid>
          {filteredActions.map((action, index) => (
            <CampaignCard key={action.id} index={index}>
              <CampaignImage image={action.image} />
              <CampaignContent>
                <CampaignTitle>{action.title}</CampaignTitle>
                <CampaignMeta>
                  <div>
                    <FontAwesomeIcon icon={faMapMarkerAlt} />
                    {action.location}
                  </div>
                  <div>
                    <FontAwesomeIcon icon={faCalendarAlt} />
                    {action.date}
                  </div>
                </CampaignMeta>

                {/* Stats section (conditionally displayed) */}
                {action.showStats ? (
                  <>
                    <ProgressBar>
                      <ProgressFill $progress={action.progress} />
                    </ProgressBar>
                    <CampaignStats>
                      <div>
                        <h4>MAD{action.raised.toLocaleString()}</h4>
                        <p>Raised</p>
                      </div>
                      <div>
                        <h4>{action.donors}</h4>
                        <p>Donors</p>
                      </div>
                      <div>
                        <h4>{action.daysLeft}</h4>
                        <p>Days Left</p>
                      </div>
                    </CampaignStats>
                  </>
                ) : (
                  <div style={{ margin: "1.5rem 0", color: "#666", fontSize: "0.9rem" }}>
                    A long-term initiative to bring positive change across Morocco.
                  </div>
                )}

                <Spacer />

                <DonateButton
                  to={`/actions/${action.id === 1 ? "morocco-portugal-beach-cleanup" : "pilot-project"
                    }`}
                >
                  Learn More
                </DonateButton>
              </CampaignContent>
            </CampaignCard>
          ))}
        </CampaignsGrid>
      </MainContent>
    </PageWrapper>
  );
};

export default CampaignsPage;
